/**
 * Fluency15Screen - K Addition Within 5 Fluency 15s Practice
 *
 * 15-second timed speed practice where student solves addition problems.
 * Hidden timer - student doesn't see countdown but timeout is tracked.
 * Pass: 90%+ accuracy over last 10 AND 8+ attempts in 2 min → fluency6Practice
 * Fail: <70% over last 10 OR 3+ timeouts in last 10 → accuracyPractice
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { useKTTS, K_QUESTION_SCREEN_AUDIO, QUESTION_SCREEN_AUDIO } from '../../../hooks/useKAudio';
import { useAuth } from '../../../context/AuthContext';
import { getAccommodationTimerDuration, isAccommodationTimerUntimed } from '../../../policies/studentAccommodationPolicy';
import { useSession } from '../../../context/SessionContext';
import { setUserProgress } from '../../../config/api';
import { SetUserProgressRequest } from '../../../types/progress';
import { progressQueue } from '../../../services/ProgressQueueService';
import { ADDITION_FACTS, AdditionFactWithId } from './types';
import AnswerBlank from '../../common/AnswerBlank';
import {
  PracticeHeader,
  PRACTICE_PHASES,
  PRACTICE_CRITERIA,
  PRACTICE_TIMER_CONSTANTS,
} from '../shared';

interface AttemptRecord {
  factId: string;
  correct: boolean;
  isTimeout: boolean;
  timestamp: number;
}

interface Fluency15ScreenProps {
  settings: KPracticeSettings;
  factsToPractice: AdditionFactWithId[];
  onComplete: () => void;
  onBack: () => void;
}

// Component to show dots for recent question presentations (within 2-minute engagement window)
const AttemptDotsIndicator: React.FC<{ attempts: AttemptRecord[] }> = ({ attempts }) => {
  // Filter to attempts within the last 2 minutes, then take last 10
  const now = Date.now();
  const windowStart = now - PRACTICE_CRITERIA.ENGAGEMENT_WINDOW_MS;
  const recentAttempts = attempts.filter(a => a.timestamp >= windowStart);
  const last10 = recentAttempts.slice(-PRACTICE_CRITERIA.ATTEMPT_WINDOW);

  const dots = [];
  for (let i = 0; i < PRACTICE_CRITERIA.ATTEMPT_WINDOW; i++) {
    const attempt = last10[i];
    let bgColor = 'bg-gray-300'; // Not yet attempted
    if (attempt) {
      bgColor = attempt.correct ? 'bg-green-500' : 'bg-red-500';
    }
    dots.push(
      <div
        key={i}
        className={`w-4 h-4 rounded-full ${bgColor} transition-colors duration-300`}
      />
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {dots}
    </div>
  );
};

// Component to show transition message (inline, not overlay)
const TransitionMessage: React.FC<{ type: 'pass' | 'fail'; message: string }> = ({ type, message }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center"
    >
      <div className="text-8xl mb-8">
        {type === 'pass' ? '🎉' : '💪'}
      </div>
      <h1 className="text-4xl lg:text-5xl font-bold">
        <span className={`bg-gradient-to-r ${
          type === 'pass'
            ? 'from-green-500 to-emerald-600'
            : 'from-blue-600 to-purple-600'
        } text-transparent bg-clip-text`}>
          {message}
        </span>
      </h1>
    </motion.div>
  );
};

const Fluency15Screen: React.FC<Fluency15ScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const { user } = useAuth();
  const timerDurationMs = getAccommodationTimerDuration(PRACTICE_TIMER_CONSTANTS.FLUENCY_15_TIMER_MS, user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const isUntimed = isAccommodationTimerUntimed(user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const { recordPageTransition } = useSession();
  const [currentFactWithId, setCurrentFactWithId] = useState<AdditionFactWithId | null>(null);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);

  // Record page transition for Caliper tracking
  useEffect(() => {
    recordPageTransition('fluency-practice');
  }, [recordPageTransition]);

  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);
  const [phase, setPhase] = useState<'showing' | 'question'>('showing');

  // Attempt history tracking for pass/fail criteria
  const [attemptHistory, setAttemptHistory] = useState<AttemptRecord[]>([]);
  const attemptHistoryRef = useRef<AttemptRecord[]>([]);

  // Track if current question presentation has been answered (to ignore retries)
  const [currentQuestionAnswered, setCurrentQuestionAnswered] = useState(false);

  // Transition message state
  const [transitionMessage, setTransitionMessage] = useState<{ type: 'pass' | 'fail'; message: string } | null>(null);

  // Hidden timer state - 15 seconds
  const [timeRemaining, setTimeRemaining] = useState<number>(timerDurationMs);
  const [timerActive, setTimerActive] = useState(false);

  // Timer tracking for attempt data
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  // Ref to store timeout IDs for cleanup
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const isFirstQuestionRef = useRef(true);

  // Ref to track if component is mounted (prevents race conditions with async operations)
  const isMountedRef = useRef(true);

  const { playTTSWithCallback, playChime, isTTSPlaying, playAudio } = useKTTS();

  // Reset timer helper function
  const resetTimer = useCallback((shouldStartTimer: boolean = false) => {
    setTimeRemaining(timerDurationMs);
    setTimerActive(shouldStartTimer && !isUntimed);
    setQuestionStartTime(Date.now());
  }, [timerDurationMs, isUntimed]);

  const currentFact = currentFactWithId ? ADDITION_FACTS[currentFactWithId.factIndex] : undefined;
  const currentSum = currentFact?.sum;

  // Select next fact, avoiding the same fact as current
  const selectNextFact = useCallback(() => {
    if (factsToPractice.length <= 1) {
      return factsToPractice[0];
    }
    let nextFact;
    do {
      const randomIndex = Math.floor(Math.random() * factsToPractice.length);
      nextFact = factsToPractice[randomIndex];
    } while (nextFact.factId === currentFactWithId?.factId);
    return nextFact;
  }, [factsToPractice, currentFactWithId?.factId]);

  // Generate answer choices (target sum + 3 other options from 0-5)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];
    const used = new Set([target]);

    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 6); // 0-5
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    return choices.sort(() => Math.random() - 0.5);
  }, []);

  // Keep attemptHistoryRef in sync
  useEffect(() => {
    attemptHistoryRef.current = attemptHistory;
  }, [attemptHistory]);

  // Check pass/fail criteria (each question presentation counts, retries don't)
  const checkCriteria = useCallback(() => {
    const history = attemptHistoryRef.current;
    const now = Date.now();
    const windowStart = now - PRACTICE_CRITERIA.ENGAGEMENT_WINDOW_MS;

    // Get last 10 question presentations
    const last10 = history.slice(-PRACTICE_CRITERIA.ATTEMPT_WINDOW);

    // Get attempts in last 2 minutes
    const recentAttempts = history.filter(a => a.timestamp >= windowStart);

    // Need at least 10 question presentations to evaluate pass/fail
    if (last10.length < PRACTICE_CRITERIA.ATTEMPT_WINDOW) {
      return { shouldPass: false, shouldFail: false };
    }

    // Calculate accuracy over last 10 questions
    const correctCount = last10.filter(a => a.correct).length;
    const accuracy = correctCount / last10.length;

    // Count timeouts in last 10
    const timeoutCount = last10.filter(a => a.isTimeout).length;

    // Check engagement (8+ questions answered in last 2 min)
    const hasEngagement = isUntimed || recentAttempts.length >= PRACTICE_CRITERIA.MIN_ENGAGEMENT_ATTEMPTS;

    // Pass criteria: 90%+ accuracy AND 8+ answers in last 2 min
    const shouldPass = accuracy >= PRACTICE_CRITERIA.PASS_ACCURACY_THRESHOLD && hasEngagement;

    // Fail criteria: Below 70% over last 10 OR 3+ timeouts in last 10
    const shouldFail = accuracy < PRACTICE_CRITERIA.FAIL_ACCURACY_THRESHOLD_FLUENCY15 ||
      timeoutCount >= PRACTICE_CRITERIA.MAX_TIMEOUTS_FOR_FAIL;

    return { shouldPass, shouldFail, last10 };
  }, [isUntimed]);

  // Handle pass - all facts to fluency6Practice
  const handlePass = useCallback(async () => {
    if (!user?.userId) {
      onComplete();
      return;
    }

    // Show transition message and play audio (reuse subitizing audio)
    setTransitionMessage({ type: 'pass', message: "Amazing! You're getting faster!" });
    if (settings.speechEnabled) {
      playAudio(QUESTION_SCREEN_AUDIO.transitions.fluency15Pass).catch(() => {});
    }

    try {
      const progressUpdate: SetUserProgressRequest = {
        facts: factsToPractice.reduce((acc, fact) => {
          acc[fact.factId] = { status: 'fluency6Practice' };
          return acc;
        }, {} as Record<string, { status: 'fluency6Practice' }>),
      };

      await setUserProgress(user.userId, progressUpdate);

      // Delay before redirect to show message (3s to allow audio to finish)
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Failed to update facts to fluency6Practice:', error);
      onComplete();
    }
  }, [user?.userId, factsToPractice, onComplete, settings.speechEnabled, playAudio]);

  // Handle fail - struggling facts to accuracyPractice
  const handleFail = useCallback(async (last10: AttemptRecord[]) => {
    if (!user?.userId) {
      onComplete();
      return;
    }

    // Show transition message and play audio (reuse subitizing audio)
    setTransitionMessage({ type: 'fail', message: "Let's practice accuracy again!" });
    if (settings.speechEnabled) {
      playAudio(QUESTION_SCREEN_AUDIO.transitions.fluency15Fail).catch(() => {});
    }

    try {
      // Get facts that were wrong or timed out in last 10
      const strugglingFactIds = new Set<string>();
      last10.forEach(attempt => {
        if (!attempt.correct || attempt.isTimeout) {
          strugglingFactIds.add(attempt.factId);
        }
      });

      if (strugglingFactIds.size === 0) {
        // No struggling facts, just reload after delay (3s to allow audio to finish)
        setTimeout(() => {
          window.location.reload();
        }, 3000);
        return;
      }

      const progressUpdate: SetUserProgressRequest = {
        facts: Array.from(strugglingFactIds).reduce((acc, factId) => {
          acc[factId] = { status: 'accuracyPractice' };
          return acc;
        }, {} as Record<string, { status: 'accuracyPractice' }>),
      };

      await setUserProgress(user.userId, progressUpdate);

      // Delay before redirect to show message (3s to allow audio to finish)
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Failed to update struggling facts:', error);
      onComplete();
    }
  }, [user?.userId, onComplete, settings.speechEnabled, playAudio]);

  // Initialize first fact
  useEffect(() => {
    if (!currentFactWithId && factsToPractice.length > 0) {
      const randomIndex = Math.floor(Math.random() * factsToPractice.length);
      setCurrentFactWithId(factsToPractice[randomIndex]);
    }
  }, [currentFactWithId, factsToPractice]);

  // Initialize when fact changes
  useEffect(() => {
    if (currentSum !== undefined) {
      setAnswerChoices(generateAnswerChoices(currentSum));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setCorrectAnswerGiven(false);
      setCurrentQuestionAnswered(false);
      setPhase('showing');
      resetTimer(false);
    }
  }, [currentFactWithId, currentSum, generateAnswerChoices, resetTimer]);

  // Start showing phase (skip delay on subsequent questions)
  useEffect(() => {
    if (phase === 'showing' && currentSum !== undefined && !transitionMessage) {
      if (isFirstQuestionRef.current) {
        const timeoutId = setTimeout(() => {
          setPhase('question');
        }, 800);
        timeoutsRef.current.push(timeoutId);
      } else {
        setPhase('question');
      }
    }
  }, [phase, currentSum, transitionMessage]);

  // Play TTS instruction when entering question phase (first question only)
  useEffect(() => {
    if (phase === 'question' && !ttsHasPlayed && currentSum !== undefined) {
      if (isFirstQuestionRef.current && settings.speechEnabled) {
        const audioFile = K_QUESTION_SCREEN_AUDIO.additionWithin5.instruction;

        playTTSWithCallback(audioFile, () => {
          setTtsHasPlayed(true);
          resetTimer(true); // Start timer after TTS finishes
        }).catch(() => {
          setTtsHasPlayed(true);
          resetTimer(true);
        });
      } else {
        setTtsHasPlayed(true);
        resetTimer(true);
      }
    }
  }, [phase, ttsHasPlayed, settings.speechEnabled, playTTSWithCallback, currentSum, resetTimer]);

  // Cleanup all timeouts on unmount and mark as unmounted
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  // Hidden countdown timer
  useEffect(() => {
    if (isUntimed || !timerActive || hasAnswered || timeRemaining <= 0) {
      if (timeRemaining <= 0 && !hasAnswered && currentFactWithId) {
        // Time expired - treat as incorrect answer (timeout)
        setHasAnswered(true);
        setTimerActive(false);

        const responseTime = Date.now() - questionStartTime;

        // Report timeout attempt data to progress queue (all attempts for analytics)
        if (user?.userId && currentFactWithId) {
          try {
            progressQueue.enqueueProgressUpdate(user.userId, {
              facts: {
                [currentFactWithId.factId]: {
                  attempts: 1,
                  correct: 0,
                  timeSpent: responseTime,
                },
              },
            });
          } catch (error) {
            console.error('Error reporting K addition fluency15 timeout:', error);
          }
        }

        // Only record FIRST attempt for this question presentation (retries don't count)
        if (!currentQuestionAnswered) {
          setCurrentQuestionAnswered(true);

          const newAttempt: AttemptRecord = {
            factId: currentFactWithId.factId,
            correct: false,
            isTimeout: true,
            timestamp: Date.now(),
          };

          setAttemptHistory(prev => {
            const updated = [...prev, newAttempt];
            attemptHistoryRef.current = updated;
            return updated;
          });
        }

        // Check criteria after recording attempt
        const timeoutId = setTimeout(() => {
          const { shouldPass, shouldFail, last10 } = checkCriteria();

          if (shouldPass) {
            handlePass();
          } else if (shouldFail && last10) {
            handleFail(last10);
          } else {
            // Continue practice - allow retry on same question (don't change fact)
            setHasAnswered(false);
            resetTimer(true);
          }
        }, PRACTICE_TIMER_CONSTANTS.WRONG_ANSWER_RETRY_MS);
        timeoutsRef.current.push(timeoutId);
      }
      return;
    }

    const timer = setTimeout(() => {
      setTimeRemaining((prev) => prev - PRACTICE_TIMER_CONSTANTS.TICK_INTERVAL_MS);
    }, PRACTICE_TIMER_CONSTANTS.TICK_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [
    isUntimed,
    timeRemaining,
    timerActive,
    hasAnswered,
    currentFactWithId,
    user,
    questionStartTime,
    checkCriteria,
    handlePass,
    handleFail,
    currentQuestionAnswered,
    resetTimer,
  ]);

  const handleAnswerSubmission = useCallback(
    (isCorrect: boolean) => {
      if (!currentFactWithId) return;

      setHasAnswered(true);
      setTimerActive(false);

      const responseTime = Date.now() - questionStartTime;

      // Report attempt data to progress queue (all attempts for analytics)
      if (user?.userId && currentFactWithId) {
        try {
          progressQueue.enqueueProgressUpdate(user.userId, {
            facts: {
              [currentFactWithId.factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: responseTime,
              },
            },
          });
        } catch (error) {
          console.error('Error reporting K addition fluency15 attempt:', error);
        }
      }

      // Only record FIRST attempt for this question presentation (retries don't count)
      if (!currentQuestionAnswered) {
        setCurrentQuestionAnswered(true);

        const newAttempt: AttemptRecord = {
          factId: currentFactWithId.factId,
          correct: isCorrect,
          isTimeout: false,
          timestamp: Date.now(),
        };

        setAttemptHistory(prev => {
          const updated = [...prev, newAttempt];
          attemptHistoryRef.current = updated;
          return updated;
        });
      }

      if (isCorrect) {
        setCorrectAnswerGiven(true);

        // Play chime and check criteria
        playChime().then(() => {
          const timeoutId = setTimeout(() => {
            const { shouldPass, shouldFail, last10 } = checkCriteria();

            if (shouldPass) {
              handlePass();
            } else if (shouldFail && last10) {
              handleFail(last10);
            } else {
              // Continue practice - select new fact
              isFirstQuestionRef.current = false;
              const nextFact = selectNextFact();
              setCurrentFactWithId(nextFact);
            }
          }, 500);
          timeoutsRef.current.push(timeoutId);
        });
      } else {
        // Incorrect answer

        // Check criteria after recording attempt
        const timeoutId = setTimeout(() => {
          const { shouldPass, shouldFail, last10 } = checkCriteria();

          if (shouldPass) {
            handlePass();
          } else if (shouldFail && last10) {
            handleFail(last10);
          } else {
            // Continue practice - allow retry on same question
            setHasAnswered(false);
            resetTimer(true);
          }
        }, PRACTICE_TIMER_CONSTANTS.WRONG_ANSWER_RETRY_MS);
        timeoutsRef.current.push(timeoutId);
      }
    },
    [currentFactWithId, playChime, user, questionStartTime, checkCriteria, handlePass, handleFail, currentQuestionAnswered, resetTimer, selectNextFact]
  );

  const handleAnswerClick = useCallback((selectedSum: number) => {
    if (isTTSPlaying || hasAnswered || !currentFactWithId) return;

    const isCorrect = selectedSum === currentSum;
    handleAnswerSubmission(isCorrect);
  }, [currentSum, hasAnswered, isTTSPlaying, currentFactWithId, handleAnswerSubmission]);

  if (!currentFactWithId || currentFact === undefined || currentSum === undefined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.FLUENCY_15}
        onBack={onBack}
        customProgress={<AttemptDotsIndicator attempts={attemptHistory} />}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-6 gap-8">
        {transitionMessage ? (
          <TransitionMessage type={transitionMessage.type} message={transitionMessage.message} />
        ) : (
          <>
            {/* Addition Equation */}
            <motion.div
              initial={isFirstQuestionRef.current ? { scale: 0, opacity: 0 } : { opacity: 0 }}
              animate={isFirstQuestionRef.current ? { scale: 1, opacity: 1 } : { opacity: 1 }}
              transition={isFirstQuestionRef.current ? { duration: 0.4 } : { duration: 0.15 }}
              className="text-6xl lg:text-7xl font-bold text-gray-800"
              style={NUMERAL_FONT_STYLE}
            >
              {currentFact.operand1} + {currentFact.operand2} = <AnswerBlank />
            </motion.div>

            {/* Showing Phase */}
            {phase === 'showing' && isFirstQuestionRef.current && (
              <div className="text-center">
                <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">
                  Solve it...
                </div>
              </div>
            )}

            {/* Question Phase - Answer Choices */}
            {phase === 'question' && (
              <div className="w-full max-w-md lg:max-w-lg">
                <div className="grid grid-cols-2 gap-8 lg:gap-12">
                  {answerChoices.map((choice, index) => (
                    <motion.button
                      key={choice}
                      initial={isFirstQuestionRef.current ? { opacity: 0, y: 30, rotate: -5 } : { opacity: 0 }}
                      animate={isFirstQuestionRef.current ? { opacity: 1, y: 0, rotate: 0 } : { opacity: 1 }}
                      transition={isFirstQuestionRef.current ? { duration: 0.4, delay: index * 0.1 } : { duration: 0.15 }}
                      whileHover={{ scale: 1.08, rotate: 1 }}
                      whileTap={{ scale: 0.92 }}
                      onClick={() => handleAnswerClick(choice)}
                      disabled={hasAnswered}
                      className={`
                        h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                        ${hasAnswered && choice === currentSum
                          ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                          : hasAnswered && choice !== currentSum
                          ? 'bg-gray-300 text-gray-500 border-4 border-gray-400'
                          : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700'
                        }
                        ${hasAnswered ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
                      `}
                      style={NUMERAL_FONT_STYLE}
                    >
                      {choice}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Fluency15Screen;
